import axios from "axios";
import { storage } from "@/lib/storage";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAY = "https://play.google.com/billing";
export const PRODUCT_KEY = (product) => `atlas.ebook.${product}`;

/** True when running inside the Android app from Google Play (TWA referrer), even without Play Billing. */
export const isPlayApp = () => {
  try {
    if (document.referrer.startsWith("android-app://")) sessionStorage.setItem("atlas.twa", "1");
    return sessionStorage.getItem("atlas.twa") === "1" || hasPlayBilling();
  } catch { return false; }
};

/** True only inside the Play-distributed TWA (Digital Goods API present). */
export const hasPlayBilling = () => typeof window !== "undefined" && typeof window.getDigitalGoodsService === "function";

async function service() {
  return window.getDigitalGoodsService(PLAY);
}

export async function playPrices(skus) {
  try { return await (await service()).getDetails(skus); } catch { return []; }
}

let _priceCache = null;
/** { sku: "1,19 €" } as charged by Google Play (tax-inclusive, localised); empty outside the Play app. */
export async function playPriceLabels(skus) {
  if (!hasPlayBilling()) return {};
  if (_priceCache) return _priceCache;
  const details = await playPrices(skus);
  const out = {};
  details.forEach((d) => { const p = d.price; if (p?.value && p?.currency) out[d.itemId] = new Intl.NumberFormat(navigator.language || "it", { style: "currency", currency: p.currency }).format(Number(p.value)); });
  _priceCache = out;
  return out;
}

const saveLicenses = (licenses) => licenses.forEach((l) => storage.set(PRODUCT_KEY(l.product), l.token));

async function verify(sku, purchaseToken) {
  const { data } = await axios.post(`${API}/play/verify`, { product_id: sku, purchase_token: purchaseToken });
  if (data.ok) saveLicenses(data.licenses);
  return data;
}

/** Buy a SKU through Google Play; resolves with the granted licences (server-verified). */
export async function playBuy(sku) {
  trackEvent("begin_checkout", { product: sku, method: "play" });
  const request = new PaymentRequest([{ supportedMethods: PLAY, data: { sku } }], { total: { label: "Google Play", amount: { currency: "EUR", value: "0" } } });
  const response = await request.show();
  const token = response.details?.purchaseToken || response.details?.token;
  const data = await verify(sku, token);
  await response.complete(data.ok ? "success" : "fail");
  if (data.ok) trackEvent("purchase", { product: sku, method: "play" });
  return data;
}

/** Restore previous Play purchases (reinstall / new device with the same Google account). */
export async function playRestore() {
  if (!hasPlayBilling()) return [];
  const purchases = await (await service()).listPurchases();
  const granted = [];
  for (const p of purchases) {
    try { const d = await verify(p.itemId, p.purchaseToken); if (d.ok) granted.push(...d.licenses); } catch { /* retry next time */ }
  }
  return granted;
}
